import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { fetchCollection } from '../lib/api';

export default function Header() {
  const [menuItems, setMenuItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    async function loadMenu() {
      const items = await fetchCollection('menu_items');
      // Sort items by order
      const sorted = (items || []).sort((a, b) => a.order - b.order);
      setMenuItems(sorted);
    }
    loadMenu();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  // Group menu items into hierarchy
  // Main items have no parent_slug
  const mainItems = menuItems.filter(item => !item.parent_slug);
  
  // Sub items are grouped by parent_slug
  const getSubItems = (slug) => {
    // Map parent slugs to match the parent's href or slug
    // e.g. parent_slug: "kompetencer" matches href: "/ydelser"
    // parent_slug: "kontakt" matches href: "/kontakt"
    if (slug === 'ydelser' || slug === '/ydelser') {
      return menuItems.filter(item => item.parent_slug === 'kompetencer');
    }
    if (slug === 'kontakt' || slug === '/kontakt') {
      return menuItems.filter(item => item.parent_slug === 'kontakt');
    }
    return [];
  };

  const isLinkActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled || location.pathname !== '/'
          ? 'bg-white border-b border-slate-200 py-4 shadow-md'
          : 'bg-white border-b border-slate-100 py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-3 group">
          <img
            src="https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/0997988e-7e2c-4c13-9f9e-4352a2ede885.png"
            alt="Møldrup VVS A/S"
            className="h-14 md:h-18 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center space-x-8">
          {mainItems.map((item) => {
            const subs = getSubItems(item.href);
            const hasSubs = subs.length > 0;
            const active = isLinkActive(item.href);

            if (hasSubs) {
              return (
                <div
                  key={item.order}
                  className="relative group py-2"
                  onMouseEnter={() => setActiveDropdown(item.order)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={item.href}
                    className={`flex items-center space-x-1 text-sm font-semibold tracking-wider uppercase transition-colors duration-200 ${
                      active ? 'text-blue-600' : 'text-slate-700 hover:text-blue-600'
                    }`}
                  >
                    <span>{item.label}</span>
                    <ChevronDown className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180" />
                  </Link>

                  {/* Dropdown Menu */}
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-slate-200 shadow-2xl py-2 rounded-none transition-all duration-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible translate-y-2 group-hover:translate-y-0">
                    {subs.map((sub) => (
                      <Link
                        key={sub.order}
                        to={sub.href}
                        className={`block px-6 py-3 text-xs font-semibold tracking-wider uppercase hover:bg-slate-50 transition-colors duration-150 ${
                          location.pathname === sub.href ? 'text-blue-600' : 'text-slate-700 hover:text-blue-600'
                        }`}
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={item.order}
                to={item.href}
                className={`text-sm font-semibold tracking-wider uppercase transition-colors duration-200 ${
                  active ? 'text-blue-600' : 'text-slate-700 hover:text-blue-600'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Hamburger Button (< 1200px) */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="min-[1200px]:hidden text-slate-700 hover:text-blue-600 p-2 focus:outline-none cursor-pointer"
          aria-label="Menu"
        >
          {isOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {/* Mobile Navigation Overlay (< 1200px) */}
      {isOpen && (
        <div className="fixed inset-0 top-[81px] md:top-[89px] bg-white z-40 min-[1200px]:hidden overflow-y-auto border-t border-slate-200">
          <nav className="px-6 py-8 flex flex-col space-y-6">
            {mainItems.map((item) => {
              const subs = getSubItems(item.href);
              const hasSubs = subs.length > 0;
              const active = isLinkActive(item.href);

              if (hasSubs) {
                const isDropdownOpen = activeDropdown === item.order;
                return (
                  <div key={item.order} className="flex flex-col space-y-2">
                    <div className="flex justify-between items-center w-full border-b border-slate-100 py-2">
                      <Link
                        to={item.href}
                        className={`text-[1.1rem] font-bold tracking-wider uppercase ${
                          active ? 'text-blue-600' : 'text-slate-800'
                        }`}
                      >
                        {item.label}
                      </Link>
                      <button
                        onClick={() => setActiveDropdown(isDropdownOpen ? null : item.order)}
                        className="p-2 text-slate-500 hover:text-blue-600 focus:outline-none cursor-pointer"
                        aria-label="Toggle dropdown"
                      >
                        <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </div>

                    {isDropdownOpen && (
                      <div className="pl-4 flex flex-col space-y-3 py-2 border-l border-slate-200">
                        {subs.map((sub) => (
                          <Link
                            key={sub.order}
                            to={sub.href}
                            className={`text-sm font-semibold tracking-wider uppercase py-1 ${
                              location.pathname === sub.href ? 'text-blue-600' : 'text-slate-600'
                            }`}
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.order}
                  to={item.href}
                  className={`text-[1.1rem] font-bold tracking-wider uppercase py-2 border-b border-slate-100 ${
                    active ? 'text-blue-600' : 'text-slate-800'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}
